import { type fabric } from "fabric"
import { type DrawableMixin, type DrawableClasses } from "../objects/drawable_mixin"
import GroupObject from "../objects/group"
import PathObject from "../objects/path"
import type SignDrawables from "../sign_drawables"
import Calc from "@/common/util/calculator"

export interface CurveOptions {
  top?: number
  left?: number
  right?: number
}

/*
 * All forms are drawn as SVG path commands with 0,0 as the center of the shape.
 * To offset shape for external protrusions use offsetX/offsetY.
 */
export default abstract class Form {
  protected readonly drawables: SignDrawables

  protected readonly flangedHeight = Calc.mmToPx(25)
  protected readonly flangedOverflow = Calc.mmToPx(1.5)
  protected readonly flangedHoleOffset = Calc.mmToPx(40)
  protected readonly flangedHoleWidth = Calc.mmToPx(15)
  protected readonly flangedHoleHeight = Calc.mmToPx(9)

  constructor(drawables: SignDrawables) {
    this.drawables = drawables
  }

  // the base of the sign
  base(skipFlanged?: boolean, klass?: DrawableClasses): DrawableMixin {
    let { widthPx: width, heightPx: height } = this.state

    const rounding = this.baseRounding
    const paths = []
    const curves: CurveOptions = {}

    let offsetY = 0

    if (this.curveTop) {
      curves.top = this.curveRadius
      offsetY = -(this.curveRadius / 2)
    }

    if (this.curveSides) {
      curves.left = this.curveRadius
      curves.right = this.curveRadius
    }

    if (this.state.fixmethod === "pole_flanged" && !skipFlanged) {
      const flanged = this.flangedPath(width, height, rounding, curves)

      if (flanged) {
        paths.push(flanged.addClass("pdfoverflow", "flangedpath"))
      }
    }

    if (this.drawables.production === "cut" && this.drawables.hasBorder) {
      width -= this.drawables.borderWidth
      height -= this.drawables.borderWidth
    }

    paths.push(this.path(width, height, rounding, curves).addClass(klass || "base"))

    const group = new GroupObject(paths, this.groupOptions)
    group.offsetY = offsetY
    return group
  }

  shadowBase(): DrawableMixin {
    return this.base(true, "shadowbase")
  }

  printAreaCutline(padding: number): DrawableMixin {
    const { widthPx, width, height, heightPx } = this.state

    padding = padding * 2

    const paths = []
    const cmds = this.commands(widthPx, heightPx)?.join("") || []

    const path = new PathObject(cmds, {
      scaleX: 1 - padding / width,
      scaleY: 1 - padding / height,
    })

    paths.push(path.addClass("printareacutline"))

    const group = new GroupObject(paths, this.groupOptions)
    return group
  }

  // the path telling printer/engraver where to cut sign
  cutline(padding: number, forClip?: boolean): DrawableMixin {
    let { widthPx: width, heightPx: height } = this.state

    const rounding = this.cutlineRounding
    const paths = []
    let offsetY = 0

    if (this.state.fixmethod === "table_stand") {
      height += Calc.mmToPx(3)
      offsetY = Calc.mmToPx(1.5)
    }

    width += padding
    height += padding

    const curves: CurveOptions = {}

    if (this.curveTop) {
      curves.top = this.curveRadius
      offsetY = -(this.curveRadius / 2)
    }

    if (this.curveSides) {
      curves.left = this.curveRadius
      curves.right = this.curveRadius
    }

    paths.push(this.cutlinePath(width, height, rounding, curves, forClip).addClass("cutline", "withoutsnap"))

    const snappable = this.snappablePaths(width, height, rounding, curves)

    if (snappable && this.state.material !== "stainless") {
      paths.push(snappable[0].addClass("cutline", "withsnap", "snappable"))
      paths.push(snappable[1].addClass("cutline", "withsnap"))
    }

    if (this.state.fixmethod === "pole_flanged") {
      const flanged = this.flangedHoles(width, height, rounding, curves)

      if (flanged) {
        paths.push(...flanged)
      }
    }

    const group = new GroupObject(paths, this.groupOptions)
    group.offsetY = offsetY

    return group
  }

  cutlineGapped(): DrawableMixin | undefined {
    const { widthPx, heightPx } = this.state

    const rounding = this.cutlineRounding
    const paths = []
    let offsetY = 0

    const gappedCutline = this.cutlineGappedPath(widthPx, heightPx, rounding)

    // different Y offset depending on where cutline legs are placed
    switch (this.state.shape) {
      case "ellipse":
        offsetY = Calc.mmToPx(1.25)
        break
      case "arched_top_bottom":
      case "stop":
        offsetY = -Calc.mmToPx(1.25)
        break
    }

    if (gappedCutline) {
      paths.push(gappedCutline.addClass("cutline", "gapped"))
      const group = new GroupObject(paths, this.groupOptions)
      group.offsetY = offsetY

      return group
    }

    return
  }

  protected get state() {
    return this.drawables.state
  }

  protected get signType() {
    return this.drawables.signType
  }

  protected get scale() {
    return this.drawables.scale
  }

  protected get isPrint() {
    return this.drawables.isPrint
  }

  protected get isEngraving() {
    return this.drawables.isEngraving
  }

  protected cutlineGappedPath(
    _width: number,
    _height: number,
    _rounding?: number,
    _curves?: CurveOptions
  ): DrawableMixin | undefined {
    return
  }

  protected abstract path(width: number, height: number, rounding?: number, curves?: CurveOptions): DrawableMixin

  protected cutlinePath(
    width: number,
    height: number,
    rounding?: number,
    curves?: CurveOptions,
    _forClip?: boolean
  ): DrawableMixin {
    return this.path(width, height, rounding, curves)
  }

  protected snappablePaths(
    _width: number,
    _height: number,
    _rounding?: number,
    _curves?: CurveOptions
  ): [DrawableMixin, DrawableMixin] | null {
    return null
  }

  protected flangedPath(
    _width: number,
    _height: number,
    _rounding?: number,
    _curves?: CurveOptions
  ): DrawableMixin | null {
    return null
  }

  protected flangedHoles(
    _width: number,
    _height: number,
    _rounding?: number,
    _curves?: CurveOptions
  ): DrawableMixin[] | null {
    return null
  }

  protected commands(_width: number, _height?: number, _rounding?: number, _curves?: CurveOptions): any[] | null {
    return null
  }

  protected get groupOptions(): fabric.IGroupOptions | undefined {
    return undefined
  }

  /*
   * https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
   * https://www.w3.org/TR/SVG/paths.html#PathDataMovetoCommands
   */

  protected pathMoveTo(x: number, y: number) {
    return "M" + x + " " + y
  }

  protected pathLineTo(x: number, y: number) {
    return ["l", x, y].join(" ")
  }

  protected pathLineToAbs(x: number, y: number) {
    return ["L", x, y].join(" ")
  }

  protected pathLineH(x: number) {
    return ["h", x].join(" ")
  }

  protected pathLineHAbs(x: number) {
    return ["H", x].join(" ")
  }

  protected pathLineV(y: number) {
    return ["v", y].join(" ")
  }

  protected pathLineVAbs(y: number) {
    return ["V", y].join(" ")
  }

  protected pathArc(rx: number, ry: number, x: number, y: number, sweep = true) {
    return ["a", rx, ry, "0", "0", sweep ? "1" : "0", x, y].join(" ")
  }

  protected pathArcAbs(rx: number, ry: number, x: number, y: number, sweep = true) {
    return ["A", rx, ry, "0", "0", sweep ? "1" : "0", x, y].join(" ")
  }

  protected pathCurveAbs(x1: number, y1: number, x2: number, y2: number, x: number, y: number) {
    return ["C", x1, y1, x2, y2, x, y].join(" ")
  }

  protected pathQuadAbs(x1: number, y1: number, x: number, y: number) {
    return ["Q", x1, y1, x, y].join(" ")
  }

  protected pathClose() {
    return "Z"
  }

  protected scaleToSize(path: fabric.Path, width: number, height: number) {
    const xDiff = width / (path.width || 0)
    const yDiff = height / (path.height || 0)

    path.scaleX = xDiff
    path.scaleY = yDiff

    return path as unknown as DrawableMixin
  }

  protected get defaultParams(): fabric.IObjectOptions {
    return { originX: "center", originY: "center" }
  }

  protected get baseRounding() {
    return 0
  }

  protected get cutlineRounding() {
    return 0
  }

  protected printCutlineRounding(radius: number) {
    const { shape, fixmethod, width, height } = this.state

    if (shape === "rect") {
      radius = fixmethod === "pole" ? Calc.mmToPx(2) : Calc.mmToPx(0.5)
    } else if (this.drawables.hasBorder) {
      if (this.drawables.signType === "aluminium_sign") {
        const mod = width >= height ? width / height : 1
        radius *= 1 + 0.55 / mod
      } else {
        radius *= 1.15
      }
    }

    return radius
  }

  protected flangedHole(x: number, y: number, direction: "vertical" | "horizontal") {
    const commands: string[] = []
    const radius = this.flangedHoleHeight / 2

    if (direction === "horizontal") {
      commands.push("M" + (x - this.flangedHoleWidth / 2 + radius) + " " + (y - this.flangedHoleHeight / 2))
      commands.push("L" + (x + this.flangedHoleWidth / 2 - radius) + " " + (y - this.flangedHoleHeight / 2))
      commands.push(
        "A" +
          radius +
          " " +
          radius +
          " 0 0 1 " +
          (x + this.flangedHoleWidth / 2 - radius) +
          " " +
          (y + this.flangedHoleHeight / 2)
      )
      commands.push("L" + (x - this.flangedHoleWidth / 2 + radius) + " " + (y + this.flangedHoleHeight / 2))
      commands.push(
        "A" +
          radius +
          " " +
          radius +
          " 0 0 1 " +
          (x - this.flangedHoleWidth / 2 + radius) +
          " " +
          (y - this.flangedHoleHeight / 2)
      )
    } else {
      commands.push("M" + (x - this.flangedHoleHeight / 2) + " " + (y - this.flangedHoleWidth / 2 + radius))
      commands.push(
        "A" +
          radius +
          " " +
          radius +
          " 0 0 1 " +
          (x + this.flangedHoleHeight / 2) +
          " " +
          (y - this.flangedHoleWidth / 2 + radius)
      )
      commands.push("L" + (x + this.flangedHoleHeight / 2) + " " + (y + this.flangedHoleWidth / 2 - radius))
      commands.push(
        "A" +
          radius +
          " " +
          radius +
          " 0 0 1 " +
          (x - this.flangedHoleHeight / 2) +
          " " +
          (y + this.flangedHoleWidth / 2 - radius)
      )
    }

    commands.push("Z")

    const hole = new PathObject(commands.join(" "))
    hole.addClass("flangedhole")

    return hole
  }

  private get curveTop() {
    return this.drawables.externalTop
  }

  private get curveSides() {
    return this.drawables.externalSides
  }

  private get curveRadius() {
    if (this.drawables.externalTop) {
      return this.drawables.externalTopPadding
    }

    if (this.drawables.externalSides) {
      return this.drawables.externalLeftPadding
    }

    return 0
  }
}
